/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { tqconstants } from "../constants/TQConstants";

export enum CacheResponseType {
    LANG_FILTER_DUMMY_ENDPOINT_PREFIX = "/langFilter",
    LANG_FILTER_DUMMY_ENDPOINT_SUFFIX = ".json",
    BYOF_LIST_ENDPOINT_PREFIX = "/byof_list",
    BYOF_LIST_ENDPOINT_SUFFIX = ".json",
    BYOF_SCRIPT_ENDPOINT_PREFIX = "/byof_script",
    BYOF_SCRIPT_ENDPOINT_SUFFIX = ".json",
    BYOF_ETAG_ENDPOINT_PREFIX = "/byof_etag",
    BYOF_ETAG_ENDPOINT_SUFFIX = ".json",
    SELECTIONS_LIST_ENDPOINT_PREFIX = "/selections_list_",
    SELECTIONS_LIST_ENDPOINT_SUFFIX = ".json",
    SELECTIONS_ETAG_ENDPOINT_PREFIX = "/selections_etag",
    SELECTIONS_ETAG_ENDPOINT_SUFFIX = ".json",
    VARIABLE_FONT_ENDPOINT_PREFIX = "/variable/",
    VARIABLE_FONT_ENDPOINT_SUFFIX = ".json",
    APP_FONT_ENDPOINT_PREFIX = "/app/",
    APP_FONT_ENDPOINT_SUFFIX = ".json",
    DEFAULT_FONT_ENDPOINT_PREFIX = "/default/",
    DEFAULT_FONT_ENDPOINT_SUFFIX = ".json",
    BYOF_FONT_ENDPOINT_PREFIX = "/byof_buffer/",
    BYOF_FONT_ENDPOINT_SUFFIX = ".json",
    ENTITLEMENT_ENDPOINT_PREFIX = "/entitlement_",
    ENTITLEMENT_ENDPOINT_SUFFIX = ".json",
    BYOF_ACCESS_ENDPOINT_PREFIX = "/byof_access",
    BYOF_ACCESS_ENDPOINT_SUFFIX = ".json",
}

export default class TQResponseStorageCache {
    private _cacheName = "tqweb-responsestorage";
    private _cacheObject: Cache | undefined = undefined;
    private _inMemoryCachedResponses = new Map<string, any>();

    public constructor() {
        this.init();
    }

    public async init() {
        try {
            this._cacheObject = await caches.open(this._cacheName);
        } catch (e) {
            // ..
        }
    }

    public async getValue(urlKey: string) {
        try {
            if (undefined == this._cacheObject) {
                this._cacheObject = await caches.open(this._cacheName);
            }
            if (this._cacheObject) {
                const res = await this._cacheObject.match(urlKey);
                if (res && res.status == tqconstants.ResponseStatus.OK) {
                    const r = await res.json();
                    return r;
                }
            }
        } catch (e) {
            // ..
        }
        return undefined;
    }

    public async putValue(urlKey: string, value: any) {
        try {
            if (this._cacheObject) {
                await this._cacheObject.put(urlKey, new Response(JSON.stringify(value)));
            }
        } catch (e) {
            // ..
        }
    }

    public async matchKey(urlKey: string) {
        let matchedKey = "";
        try {
            if (this._cacheObject) {
                const keys = await this._cacheObject.keys();
                for (const key of keys) {
                    if (key.url.indexOf(urlKey) != -1) {
                        matchedKey = key.url.substring(key.url.indexOf(urlKey));
                    }
                }
            }
        } catch (e) {
            // ..
        }
        return matchedKey;
    }

    public async getBlobValue(urlKey: string) {
        try {
            if (undefined == this._cacheObject) {
                this._cacheObject = await caches.open(this._cacheName);
            }
            if (this._cacheObject) {
                const res = await this._cacheObject.match(urlKey);
                if (res && res.status == tqconstants.ResponseStatus.OK) {
                    const r = await res.blob();
                    return r;
                }
            }
        } catch (e) {
            // ..
        }
        return undefined;
    }

    public async putBlobValue(urlKey: string, value: any) {
        try {
            if (this._cacheObject) {
                await this._cacheObject.put(urlKey, new Response(value));
            }
        } catch (e) {
            // ..
        }
    }

    public async deleteValue(urlKey: string) {
        try {
            if (this._cacheObject) {
                await this._cacheObject.delete(urlKey);
            }
        } catch (e) {
            // ..
        }
    }

    public async getInMemoryOrFromResponseCache(key: string, isBlob = false) {
        let valIfExists = this._inMemoryCachedResponses.get(key);
        if (valIfExists == undefined || valIfExists == null) {
            if (isBlob) {
                valIfExists = await this.getBlobValue(key);
            } else {
                valIfExists = await this.getValue(key);
            }
            this._inMemoryCachedResponses.set(key, valIfExists);
        }
        return valIfExists;
    }
}
